import { create } from "zustand";
import { persist } from "zustand/middleware";

/**
 * Check if a JWT token is expired (with 30s buffer).
 * Returns true if expired or invalid.
 */
const isTokenExpired = (token) => {
  if (!token) return true;
  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    return payload.exp * 1000 < Date.now() - 30000;
  } catch {
    return true;
  }
};

/**
 * Extract company from JWT token.
 * Returns 'rbs' as default if company claim is not found.
 */
const extractCompanyFromToken = (token) => {
  if (!token) return "rbs";
  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    return payload.company || "rbs";
  } catch {
    return "rbs";
  }
};

export const useAuthStore = create(
  persist(
    (set, get) => ({
      // State
      user: null,
      accessToken: null,
      // refreshToken removed - now stored in httpOnly cookie
      isAuthenticated: false,
      isLoading: false,
      company: "rbs", // Company identifier (rbs or sby) from JWT token

      // Actions
      login: async (loginResponse) => {
        try {
          // loginResponse should contain: { accessToken, expiresIn, company }
          // refreshToken is now in httpOnly cookie, not in response
          const { accessToken, company } = loginResponse;

          // Extract company from loginResponse or from JWT token
          const companyFromResponse = company || extractCompanyFromToken(accessToken);

          // Set access token and company
          set({
            accessToken,
            company: companyFromResponse,
            isAuthenticated: true,
            isLoading: false,
          });

          // Then get user info using the access token (this will use the token from the store)
          const { apiClient } = await import("../services/api");
          const userInfo = await apiClient.get("/auth/whoAmI");

          set((state) => ({
            ...state,
            user: userInfo,
            // Update company from whoAmI response if available
            company: userInfo.company || state.company,
          }));

          return { success: true };
        } catch (error) {
          set({
            isLoading: false,
            isAuthenticated: false,
            accessToken: null,
            company: "rbs",
          });
          return { success: false, error: error.message };
        }
      },

      logout: () => {
        set({
          user: null,
          accessToken: null,
          isAuthenticated: false,
          isLoading: false,
          company: "rbs",
        });
      },

      updateUser: (userData) => {
        set((state) => ({
          user: { ...state.user, ...userData },
        }));
      },

      // Update tokens after refresh (only access token now)
      updateTokens: (accessToken) => {
        const company = extractCompanyFromToken(accessToken);
        set({ accessToken, company });
      },

      // Set loading state
      setLoading: (isLoading) => {
        set({ isLoading });
      },

      // Getters
      getAccessToken: () => get().accessToken,
      getUser: () => get().user,
      getCompany: () => get().company,

      // Legacy getter for backward compatibility
      getToken: () => get().accessToken,
    }),
    {
      name: "auth-storage",
      partialize: (state) => ({
        user: state.user,
        accessToken: state.accessToken,
        isAuthenticated: state.isAuthenticated,
        company: state.company,
      }),
      onRehydrateStorage: () => (state, error) => {
        if (error) return;
        const apiBase = process.env.REACT_APP_API_BASE_URL || "http://localhost:8080/api";
        // After restoring persisted state, check if the token is still valid
        if (state && state.isAuthenticated && isTokenExpired(state.accessToken)) {
          // Token expired — try refresh, if that fails redirect to sign-in
          fetch(`${apiBase}/auth/token/refresh`, {
            method: "POST",
            credentials: "include",
          })
            .then((res) => {
              if (!res.ok) throw new Error("Refresh failed");
              return res.json();
            })
            .then((data) => {
              // Refresh succeeded — update the access token and extract company
              const company = extractCompanyFromToken(data.accessToken);
              useAuthStore.setState({ accessToken: data.accessToken, company });
            })
            .catch(() => {
              // Refresh failed — clear auth and redirect
              useAuthStore.setState({
                user: null,
                accessToken: null,
                isAuthenticated: false,
                isLoading: false,
              });
              if (window.location.pathname !== "/sign-in" && window.location.pathname !== "/sign-up") {
                window.location.href = "/sign-in";
              }
            });
        } else if (state && state.isAuthenticated && state.accessToken) {
          // Token still valid — call whoAmI to get the authoritative company from
          // the backend being served. This corrects stale localStorage values when
          // the same browser switches between rbs/sby/dev-1 ports.
          fetch(`${apiBase}/auth/whoAmI`, {
            headers: { Authorization: `Bearer ${state.accessToken}` },
          })
            .then((res) => (res.ok ? res.json() : null))
            .then((data) => {
              if (data?.company) {
                useAuthStore.setState({ company: data.company, user: data });
              }
            })
            .catch(() => {});
        }
      },
    }
  )
);
